/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { format } from 'date-fns'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import Radom, {
  IInvoice, IManagedPaymentMethodDetails, IManagedPaymentStatus, LineItem, PriceQuote, ProductWithQuantity, Profile
} from '../api/Radom'
import { zeroTravelRuleFieldsCollected, CheckoutData, TravelRuleFieldsCollected } from './Checkout'
import PayWidget, { CheckoutParams, CheckoutState, determineTrfCollectionState } from './PayWidget'
import Spinner from './Spinner'
import Checkmark from '../icons/Checkmark'
import { RADOM_COLORS, WINDOW_SIZE } from '../util/Constants'
import { formatCurrency } from '../util/Currencies'
import { convertItem, getTotalSum } from '../util/Products'
import { CheckmarkWrapper } from '../components/Receipt'
import { shortAddress } from '../util/Util'
import { Chevron } from '../icons/Chevron'
import RadomLogo from '../icons/Logo2'
import TravelRuleFieldsWidget from './TravelRuleFieldsWidget'
import { trackCheckoutFunnelStep } from '../util/CheckoutFunnel'
import { PrimaryButton } from './Button'
import PageLoadState from './PageLoadState'

export const StatusBadge = styled.div<{ status: 'pending' | 'paid' | 'voided' | 'overdue' }>`
  text-transform: capitalize;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  color: white;
  font-size: 12px;
  font-weight: 600;

  ${({ status }) => {
    let bgColor = RADOM_COLORS.ERROR_LIGHT as string
    let textColor = RADOM_COLORS.ERROR as string

    switch (status) {
    case 'paid':
      bgColor = RADOM_COLORS.SUCCESS_LIGHT as string
      textColor = RADOM_COLORS.SUCCESS as string
      break
    case 'pending':
      bgColor = RADOM_COLORS.GRAY_DARK
      textColor = RADOM_COLORS.GRAY_DARKEST as string
      break
    }
    return `
      background-color: ${bgColor};
      color: ${textColor};
    `
  }};
`

const LineItemGrid = styled.div`
  display: grid;
  align-items: start;
  grid-template-columns: 8fr 1fr;
  row-gap: 18px;
  padding: 20px 0px;
  font-size: 14px;

  > *:nth-child(2n) {
    text-align: right;
  }
`

export const Card = styled.div`
  font-size: 14px;
  padding: 26px;
  border-radius: 12px;
  background-color: white;
  border: 1px solid #e2e8f3;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 20px;
  }
`

export const Container = styled.div`
  max-width: 760px;
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 30px 22px;
  animation: fadeSlide 0.5s ease;

  @keyframes fadeSlide {
    from {
      opacity: 0;
      transform: translateY(-25%);
    }

    to {
      opacity: 100%;
      transform: translateY(0%);
    }
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 18px;
  }
`

const InvoiceHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #e6ebf4;
`

const InvoiceHero = styled.div`
  padding: 28px 24px 24px;
  border-bottom: 1px solid #e6ebf4;
  background: #f8fafd;
`

const InvoiceCompactSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  padding: 18px 24px;
  background: #f8fafd;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
`

const InvoiceCompactAmount = styled.div`
  font-size: 26px;
  font-weight: 600;
  line-height: 1.05;
  color: #0f172a;
`

const InvoiceMetaGrid = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr;
  margin: 0 0 18px;
  row-gap: 8px;
  column-gap: 16px;

  > *:nth-child(2n+1) {
    color: ${RADOM_COLORS.GRAY_DARKER};
  }
`

const ViewInvoiceButton = styled.div`
  cursor: pointer;
  margin-top: 18px;
  color: #67768d;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 5px;

  :hover {
    opacity: 0.5;
  }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #67768d;
  font-size: 13px;
  padding: 2px 0 8px;
  flex-wrap: wrap;
`

// ---------------------------------------------------------------------------------------------------------------------
// Determine which travel rule fields need to be collected
const determineMissingTrfs = (customer: Profile): TravelRuleFieldsCollected => {
  const addressParts = (customer.billingAddress ?? '')
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0)

  const hasLine1 = addressParts.length >= 1
  const hasTownOrCity = addressParts.length >= 2
  const hasState = addressParts.length >= 3
  const hasPostalCode = addressParts.length >= 4
  const hasLine2 = addressParts.length >= 5

  return {
    accountType: customer.accountType,
    countryOfResidenceCollected: Boolean(customer.residenceCountry),
    dateOfBirthCollected: Boolean(customer.dob),
    emailAddressCollected: Boolean(customer.email),
    nameCollected: Boolean(customer.name),
    companyNameCollected: Boolean(customer.companyName),
    addressLine1Collected: hasLine1,
    addressLine2Collected: hasLine2,
    townOrCityCollected: hasTownOrCity,
    postalCodeCollected: hasPostalCode,
    stateOrProvinceCollected: hasState
  }
}

// ---------------------------------------------------------------------------------------------------------------------
interface IProps {
  invoiceId?: string
  previewData?: IInvoice
  hideTravelRuleFields?: boolean
}

// ---------------------------------------------------------------------------------------------------------------------
const PayInvoice = (props: IProps): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [invoiceData, setInvoice] = useState<IInvoice>()
  const [priceQuotes, setPriceQuotes] = useState<PriceQuote[]>([])
  const [totalSum, setTotalSum] = useState(0)
  const [currency, setCurrency] = useState('USD')

  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const [hasStartedInvoicePayment, setHasStartedInvoicePayment] = useState(false)
  const [isPreProcessingPayment, setIsPreProcessingPayment] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [managedPaymentDetails, setManagedPaymentDetails] = useState<IManagedPaymentMethodDetails>()
  const [successElement, setSuccessElement] = useState<React.ReactElement>(<></>)
  const [paymentStatus, setPaymentStatus] = useState<IManagedPaymentStatus>()
  const trackedFunnelStepsRef = useRef<Set<string>>(new Set())
  const paymentPanelRef = useRef<HTMLDivElement | null>(null)

  // Start by assuming that none of the travel rule fields have been collected
  const [collectedTrfs, setCollectedTrfs] = useState<TravelRuleFieldsCollected>(zeroTravelRuleFieldsCollected())
  const [checkoutState, setCheckoutState] = useState<CheckoutState>(CheckoutState.CollectTravelRuleFields)

  const scrollToPaymentPanel = (): void => {
    setHasStartedInvoicePayment(true)
    setDetailsExpanded(false)
    setTimeout(() => {
      paymentPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      const firstFocusable = paymentPanelRef.current?.querySelector<HTMLElement>(
        'input, button, [tabindex]:not([tabindex="-1"])'
      )
      firstFocusable?.focus()
    }, 0)
  }

  const getQuotes = async (invoice: { products: ProductWithQuantity[], lineItems: LineItem[] }): Promise<void> => {
    const productCurrencies: string[] = [
      ...new Set([...invoice.products.map(p => p.product.currency) ?? []]),
      ...invoice.lineItems.map(l => l.currency ?? 'USD') ?? []
    ]
    if (productCurrencies.length === 0) return
    if (productCurrencies.every(c => priceQuotes.findIndex(q => q.from === c && q.to === currency) !== -1)) {
      return
    }
    setIsLoading(true)
    try {
      const quotes = await Radom.getPriceQuotes(productCurrencies, [currency])
      if (quotes) {
        setPriceQuotes(quotes)
      }
    } catch (err) {
      console.error('Failed to load price quotes', err)
    } finally {
      setIsLoading(false)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  const checkoutData: CheckoutData = useMemo(() => {
    const c: CheckoutData = {
      isInvoice: true,
      sellerAddress: '',
      sellerLogoUrl: '',
      sellerName: '',
      products: [],
      lineItems: [],
      gateway: {},
      customizations: {
        allowFreeInputAmount: false,
        allowDiscountCodes: false,
        slantedEdge: false
      },
      chargeCustomerNetworkFee: false,
      travelRuleFields: {},
      travelRuleEmail: ''
    }

    if (invoiceData) {
      c.sellerAddress = shortAddress(invoiceData.sellerAddress, 6)
      if (invoiceData.seller) {
        c.sellerLogoUrl = invoiceData.seller.logoUrl ?? ''
        c.sellerName = invoiceData.seller.name || invoiceData.seller.email
      }
      c.products = invoiceData.products
      c.lineItems = invoiceData.lineItems?.map(l => {
        return {
          ...l,
          currency: l.currency ?? 'USD',
          chargingIntervalSeconds: 0
        }
      })
      c.gateway = invoiceData.gateway

      // Adopt the customer email address as the travel rule email value
      c.travelRuleEmail = invoiceData.customer.email
    }
    return c
  }, [invoiceData, priceQuotes])

  // -------------------------------------------------------------------------------------------------------------------
  const onCheckout = async ({ selfCustodial, managed, priceQuotes }: CheckoutParams): Promise<void> => {
    if (props.previewData) return
    setErrorMessage('')

    if (!invoiceData) {
      setErrorMessage('Payment link data not set. Please refresh and try again.')
      return
    }

    if (managed) {
      setHasStartedInvoicePayment(true)
      setIsPreProcessingPayment(true)

      const { selectedMethod: method } = managed

      try {
        if (!invoiceData) throw new Error('Invoice not found')

        if (!trackedFunnelStepsRef.current.has('paymentInitiated')) {
          trackedFunnelStepsRef.current.add('paymentInitiated')
          void trackCheckoutFunnelStep({
            sourceType: 'invoice',
            sourceId: invoiceData.id,
            rail: 'crypto',
            step: 'paymentInitiated',
            metadata: {
              network: method.tokenInfo?.networkName ?? method.name,
              tokenAddress: method.tokenInfo?.tokenAddress
            }
          })
        }

        const res = await Radom.createInvoicePayment({
          invoiceId: invoiceData.id,
          paymentMethod: {
            managed: {
              method: {
                network: method.tokenInfo?.networkName ?? method.name,
                token: method.tokenInfo?.tokenAddress ?? undefined
              }
            }
          }
        })

        if (!res.paymentMethodDetails.managed) {
          throw new Error('Unexpected empty managed payment method details')
        }

        setManagedPaymentDetails(res.paymentMethodDetails.managed)

        const checkPaymentInterval = setInterval(() => {
          if (!res.paymentMethodDetails.managed) {
            return
          }

          Radom.getManagedPaymentStatus(res.paymentMethodDetails.managed?.paymentId).then(res => {
            setPaymentStatus(res)

            if (res.status === 'paymentConfirmed') {
              if (!trackedFunnelStepsRef.current.has('paymentConfirmed')) {
                trackedFunnelStepsRef.current.add('paymentConfirmed')
                void trackCheckoutFunnelStep({
                  sourceType: 'invoice',
                  sourceId: invoiceData.id,
                  rail: 'crypto',
                  step: 'paymentConfirmed'
                })
              }
              setIsSuccess(true)
              clearInterval(checkPaymentInterval)

              setSuccessElement(
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <CheckmarkWrapper>
                    <Checkmark width={30} height={30} animationDelay='0.5s' animationSpeed='0.75s' />
                  </CheckmarkWrapper>
                  <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment successful</span>
                </div>
              )
            }

            if (res.status === 'failed') {
              if (!trackedFunnelStepsRef.current.has('paymentFailed')) {
                trackedFunnelStepsRef.current.add('paymentFailed')
                void trackCheckoutFunnelStep({
                  sourceType: 'invoice',
                  sourceId: invoiceData.id,
                  rail: 'crypto',
                  step: 'paymentFailed'
                })
              }
              clearInterval(checkPaymentInterval)
            }
          }).catch(() => { })
        }, 1000)
      } catch (err) {
        console.error(err)
        setErrorMessage(err.reason || err.message)
      }

      setIsPreProcessingPayment(false)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  const onQuoteUpdate = async (): Promise<void> => {
    if (invoiceData) {
      getQuotes(invoiceData)
    }
  }

  const loadInvoiceData = async (): Promise<void> => {
    setLoadError('')
    setIsLoading(true)
    try {
      if (props.invoiceId) {
        const invoice = await Radom.getInvoice(props.invoiceId)
        const trfValues = determineMissingTrfs(invoice.customer)
        setCollectedTrfs(trfValues)
        setCheckoutState(determineTrfCollectionState(trfValues))
        setInvoice(invoice)
        await getQuotes(invoice)
      }

      if (props.previewData) {
        setInvoice(props.previewData)
        await getQuotes(props.previewData)
      }
    } catch (err: any) {
      console.error('Failed to load invoice', err)
      setLoadError(err?.response?.data?.error ?? err?.message ?? 'Unable to load invoice right now.')
    } finally {
      setIsLoading(false)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    document.title = 'Radom - Invoice'
    void loadInvoiceData()
  }, [props.invoiceId, props.previewData])

  useEffect(() => {
    setHasStartedInvoicePayment(false)
    setDetailsExpanded(false)
  }, [invoiceData?.id])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (checkoutData) {
      let primaryCurrency: string = 'USD'
      if (checkoutData.products && checkoutData.products?.length > 0) {
        primaryCurrency = checkoutData.products[0].product.currency
      } else if (checkoutData.lineItems && checkoutData.lineItems.length > 0 && checkoutData.lineItems[0].currency) {
        primaryCurrency = checkoutData.lineItems[0].currency
      }
      setCurrency(primaryCurrency)
      const [sum] = getTotalSum(checkoutData, priceQuotes, primaryCurrency)
      setTotalSum(sum)
    }
  }, [checkoutData])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!invoiceData) {
      return
    }

    if (trackedFunnelStepsRef.current.has('viewed')) {
      return
    }

    trackedFunnelStepsRef.current.add('viewed')
    void trackCheckoutFunnelStep({
      sourceType: 'invoice',
      sourceId: invoiceData.id,
      rail: 'crypto',
      step: 'viewed'
    })
  }, [invoiceData?.id])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (props.previewData) {
      let primaryCurrency: string = 'USD'
      if (props.previewData.products && props.previewData.products.length > 0) {
        primaryCurrency = props.previewData.products[0].product.currency
      } else if (props.previewData.lineItems &&
        props.previewData.lineItems.length > 0 &&
        props.previewData.lineItems[0].currency
      ) {
        primaryCurrency = props.previewData.lineItems[0].currency
      }
      setCurrency(primaryCurrency)
    }
  }, [props.previewData])

  if (loadError) {
    return <PageLoadState
      title="Invoice unavailable"
      message={loadError}
      onRetry={loadInvoiceData}
    />
  }

  if (!invoiceData && !props.previewData) {
    return <div style={{
      display: 'flex',
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Spinner />
    </div>
  }

  const sellerDisplayName =
    invoiceData?.seller?.name && invoiceData.seller.name.length > 0
      ? invoiceData.seller.name
      : shortAddress(invoiceData?.sellerAddress ?? '', 4)

  const invoiceIdentifier = props.invoiceId ?? invoiceData?.id ?? 'example-invoice-uuid'

  // -------------------------------------------------------------------------------------------------------------------
  return <div style={{
    minWidth: '100vw',
    minHeight: '100vh',
    backgroundColor: '#f6f9fc'
  }}>
    <Container>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <InvoiceHeaderRow>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: RADOM_COLORS.GRAY_DARKER }}>
            {
              checkoutData.sellerLogoUrl &&
              <img src={checkoutData.sellerLogoUrl} style={{ maxWidth: 90, maxHeight: 26 }} />
            }
            <span>{sellerDisplayName}</span>
          </div>
          {
            invoiceData?.status &&
            <StatusBadge status={invoiceData.status}>
              {invoiceData.status}
            </StatusBadge>
          }
        </InvoiceHeaderRow>

        {hasStartedInvoicePayment && !detailsExpanded
          ? (
            <InvoiceCompactSummary>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 13, color: '#67768d', fontWeight: 500 }}>
                  Invoice from {sellerDisplayName}
                </div>
                {
                  !isLoading
                    ? <InvoiceCompactAmount>{formatCurrency(currency, totalSum)}</InvoiceCompactAmount>
                    : <Spinner />
                }
                {invoiceData?.overdueAt &&
                  <div style={{ fontSize: 13, color: RADOM_COLORS.GRAY_DARKER }}>
                    Due {format(new Date(invoiceData.overdueAt), 'MMMM do, yyyy')}
                  </div>}
              </div>
              <ViewInvoiceButton
                onClick={() => setDetailsExpanded(true)}
                style={{ marginTop: 0, flexShrink: 0 }}
              >
                <span>View invoice</span>
                <Chevron style={{ opacity: 0.4, rotate: '0deg', transition: '0.2s ease all' }} />
              </ViewInvoiceButton>
            </InvoiceCompactSummary>
          )
          : (
            <>
              <InvoiceHero>
                <div style={{ fontSize: 14, color: '#67768d', marginBottom: 8, fontWeight: 500 }}>
                  Invoice from {sellerDisplayName}
                </div>
                {
                  !isLoading
                    ? <div style={{ fontSize: 46, fontWeight: 600, lineHeight: 1.06, color: '#0f172a' }}>
                      {formatCurrency(currency, totalSum)}
                    </div>
                    : <Spinner />
                }
                {invoiceData?.overdueAt &&
                  <div style={{
                    marginTop: 8,
                    fontSize: 16,
                    color: RADOM_COLORS.GRAY_DARKER
                  }}>
                    Due {format(new Date(invoiceData.overdueAt), 'MMMM do, yyyy')}
                  </div>}
              </InvoiceHero>

              <div style={{ padding: 22 }}>
                <InvoiceMetaGrid>
                  <div>Invoice</div>
                  <div>{invoiceIdentifier}</div>
                  <div>To</div>
                  <div>{invoiceData?.customer.name || invoiceData?.customer.email}</div>
                  <div>From</div>
                  <div>{sellerDisplayName}</div>
                  {invoiceData?.memo && <>
                    <div>Memo</div>
                    <div>{invoiceData.memo}</div>
                  </>}
                </InvoiceMetaGrid>

                {!hasStartedInvoicePayment && (
                  <PrimaryButton
                    disabled={invoiceData?.status === 'voided' || invoiceData?.status === 'paid'}
                    style={{
                      width: '100%',
                      background: '#0a2540',
                      borderRadius: 8
                    }}
                    onClick={scrollToPaymentPanel}
                  >
                    Pay this invoice
                  </PrimaryButton>
                )}

                <ViewInvoiceButton onClick={() => setDetailsExpanded(!detailsExpanded)}>
                  <span>
                    {detailsExpanded ? 'Hide' : 'Show'} invoice details
                  </span>
                  <Chevron style={{ opacity: 0.4, rotate: detailsExpanded ? '180deg' : '0deg', transition: '0.2s ease all' }} />
                </ViewInvoiceButton>

                <div style={{
                  transition: '0.35s ease',
                  overflow: 'hidden',
                  maxHeight: `${detailsExpanded ? '280px' : '0'}`
                }}>
                  <LineItemGrid>
                    {
                      invoiceData?.products.map((line, index) => (
                        <React.Fragment key={`product-${line.product.id || index}`}>
                          <div>
                            <div>{line.product.name}</div>
                            <div style={{ color: RADOM_COLORS.GRAY_DARKER }}>Qty {line.quantity ?? 1}</div>
                          </div>
                          <div>
                            {
                              isLoading && <Spinner />
                            }
                            {
                              !isLoading && <div>
                                {formatCurrency(line.product.currency, line.product.price * (line.quantity ?? 1))}
                                {
                                  line.product.currency !== currency &&
                                  <div style={{ color: RADOM_COLORS.GRAY_DARKER }}>{
                                    formatCurrency(currency,
                                      convertItem({ ...line.product, quantity: line.quantity }, currency, priceQuotes)
                                    )
                                  }</div>
                                }
                              </div>
                            }
                          </div>
                        </React.Fragment>
                      ))
                    }
                    {
                      invoiceData?.lineItems?.map((line, index) => (
                        <React.Fragment key={`line-item-${line.name}-${index}`}>
                          <div>
                            <div>{line.name}</div>
                            <div style={{ color: RADOM_COLORS.GRAY_DARKER }}>Qty {line.quantity ?? 1}</div>
                          </div>
                          <div>
                            {formatCurrency(line.currency ?? 'USD', line.price * (line.quantity ?? 1))}
                            {
                              line.currency !== currency &&
                              <div style={{ color: RADOM_COLORS.GRAY_DARKER }}>{
                                formatCurrency(currency,
                                  convertItem(line, currency, priceQuotes)
                                )
                              }</div>
                            }
                          </div>
                        </React.Fragment>
                      ))
                    }
                  </LineItemGrid>
                </div>
              </div>
            </>
          )}
      </Card>
      <Card ref={paymentPanelRef} style={{ scrollMarginTop: 16 }}>
        {
          !props.hideTravelRuleFields && checkoutState === CheckoutState.CollectTravelRuleFields && invoiceData?.status !== 'paid' &&
          <TravelRuleFieldsWidget
            travelRuleFieldsCollected={collectedTrfs}
            prefilledData={invoiceData?.customer}
            accountType={invoiceData?.customer.accountType}
            onTravelRuleFieldsComplete={async collectedTrfs => {
              if (!invoiceData) {
                return
              }

              try {
                await Radom.updateTrfsForCustomerViaInvoice(invoiceData.id, collectedTrfs)
                setCheckoutState(CheckoutState.Checkout)
                setHasStartedInvoicePayment(true)
                setDetailsExpanded(false)

                if (!trackedFunnelStepsRef.current.has('travelRuleCompleted')) {
                  trackedFunnelStepsRef.current.add('travelRuleCompleted')
                  void trackCheckoutFunnelStep({
                    sourceType: 'invoice',
                    sourceId: invoiceData.id,
                    rail: 'crypto',
                    step: 'travelRuleCompleted'
                  })
                }
              } catch (err: any) {
                setErrorMessage(err?.message ?? 'Failed to save billing details. Please try again.')
              }
            }}
          />
        }

        {
          (props.hideTravelRuleFields || checkoutState !== CheckoutState.CollectTravelRuleFields) &&
          (invoiceData?.status === 'pending' || invoiceData?.status === 'overdue' || props.previewData) &&
          <PayWidget
            currency={currency}
            checkoutData={checkoutData}
            onCheckout={onCheckout}
            onQuoteUpdate={async () => await onQuoteUpdate()}
            isSuccess={isSuccess}
            isPreProcessingPayment={isPreProcessingPayment}
            errorMessage={errorMessage}
            managedPaymentDetails={managedPaymentDetails}
            onChangePaymentMethod={() => {
              setManagedPaymentDetails(undefined)
              setPaymentStatus(undefined)
              setErrorMessage('')
              setIsPreProcessingPayment(false)
            }}
            successElement={successElement}
            paymentStatus={paymentStatus}
            hideTravelRuleFields={!!props.previewData}
          />
        }
        {
          invoiceData?.status === 'voided' &&
          <div style={{ fontSize: 16, color: RADOM_COLORS.ERROR, textAlign: 'center' }}>
            Invoice has been voided
          </div>
        }
        {
          invoiceData?.status === 'paid' &&
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            userSelect: 'none'
          }}>
            <CheckmarkWrapper style={{ width: 20, height: 20 }}>
              <Checkmark width={20} height={20} animationDelay='0.5s' animationSpeed='0.75s' />
            </CheckmarkWrapper>
            <div style={{ fontSize: 16, color: RADOM_COLORS.NICE_GREEN, textAlign: 'center' }}>
              Invoice has been paid
            </div>
          </div>
        }
      </Card>
      <Footer>
        <a
          href='https://radom.com'
          target='_blank'
          rel='noreferrer'
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 500
          }}
        >
          <span>Powered by</span>
          <RadomLogo width={48} fill='#67768d' />
        </a>
        <span style={{ opacity: 0.5 }}>|</span>
        <a
          href='https://www.radom.com/terms'
          target='_blank'
          rel='noreferrer'
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          Terms
        </a>
        <span style={{ opacity: 0.5 }}>|</span>
        <a
          href='https://radom.com/privacy'
          target='_blank'
          rel='noreferrer'
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          Privacy
        </a>
      </Footer>
    </Container>
  </div>
}

export default PayInvoice
